"use client";

import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { preprocessVideoFrame } from '@/utils/imagePreprocessor';
import { registerStream, stopAllStreams } from '@/utils/mediaManager';

// BarcodeScanner: componente reutilizable, no modal, para integración directa
export default function BarcodeScanner({ onDetected, className }: { onDetected: (code: string) => void; className?: string }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const drawRafRef = useRef<number | null>(null);
  const detectorRef = useRef<any | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const detectedRef = useRef<boolean>(false);
  const scanSessionRef = useRef<number>(0);
  const poweredOffRef = useRef<boolean>(false);
  const LAST_DEVICE_KEY = 'barcode_scanner_last_device';
  const [showCamera, setShowCamera] = useState(false);

  // Enumerar cámaras disponibles
  const enumerateDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      setDevices(devices.filter(d => d.kind === 'videoinput'));
    } catch (e) {
      setDevices([]);
    }
  };

  useEffect(() => { enumerateDevices(); }, []);

  // Iniciar escaneo
  const startScan = async (deviceId?: string | null) => {
    poweredOffRef.current = false;
    detectedRef.current = false;
    scanSessionRef.current += 1;
    const mySession = scanSessionRef.current;
    let stream: MediaStream | null = null;
    setError(null);
    try { stopAllStreams(); } catch (_) {}
    try {
      if (!navigator || !(navigator as any).mediaDevices || typeof (navigator as any).mediaDevices.getUserMedia !== 'function') {
        setError('Cámara no disponible');
        setScanning(false);
        return;
      }
      const strategies: Array<any> = [];
      if (deviceId) strategies.push({ video: { deviceId: { exact: deviceId } } });
      strategies.push({ video: { facingMode: 'environment' } });
      strategies.push({ video: true });
      let lastErr: any = null;
      for (const s of strategies) {
        try {
          stream = await navigator.mediaDevices.getUserMedia(s);
          if (mySession !== scanSessionRef.current || poweredOffRef.current) {
            try { stream.getTracks().forEach(t => t.stop()); } catch (_) {}
            stream = null;
            break;
          }
          break;
        } catch (e) { lastErr = e; }
      }
      if (!stream) {
        setError('No se pudo iniciar la cámara');
        setScanning(false);
        return;
      }
      if (poweredOffRef.current) {
        try { stream.getTracks().forEach(t => t.stop()); } catch (_) {}
        return;
      }
      if (videoRef.current) {
        try {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            if (canvasRef.current) {
              canvasRef.current.width = videoRef.current!.videoWidth || 640;
              canvasRef.current.height = videoRef.current!.videoHeight || 480;
              canvasRef.current.style.width = '100%';
              canvasRef.current.style.height = 'auto';
            }
          };
          await videoRef.current.play();
        } catch (e) { setError('No se pudo mostrar la cámara'); }
      }
      stream && registerStream(stream);
      setScanning(true);
      // BarcodeDetector nativo
      if ('BarcodeDetector' in window) {
        const Native = (window as any).BarcodeDetector;
        detectorRef.current = new Native({ formats: ['ean_13', 'ean_8', 'code_128', 'qr_code'] });
      } else {
        detectorRef.current = null;
      }
      // Loop de escaneo
      const scanLoop = async () => {
        if (!videoRef.current || !scanning || poweredOffRef.current) return;
        try {
          let detectionTarget: HTMLCanvasElement | HTMLVideoElement | null = null;
          if (canvasRef.current && videoRef.current) {
            preprocessVideoFrame(videoRef.current, canvasRef.current, {
              contrast: 1.6, brightness: 18, saturation: 1.1, blur: 0.4, sharpen: 0.35, gamma: 1.2, binarize: false,
            });
            detectionTarget = canvasRef.current;
          }
          if (detectorRef.current && detectionTarget) {
            const codes = await detectorRef.current.detect(detectionTarget);
            if (codes && codes.length > 0 && !detectedRef.current) {
              detectedRef.current = true;
              onDetected(codes[0].rawValue || codes[0].value || '');
              stopScan();
              return;
            }
          }
        } catch (e) {}
        rafRef.current = requestAnimationFrame(scanLoop);
      };
      rafRef.current = requestAnimationFrame(scanLoop);
    } catch (e) {
      setError('Error al iniciar la cámara');
      setScanning(false);
    }
  };

  const stopScan = () => {
    setScanning(false);
    poweredOffRef.current = true;
    try { stopAllStreams(); } catch (_) {}
    if (videoRef.current) videoRef.current.srcObject = null;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (drawRafRef.current) cancelAnimationFrame(drawRafRef.current);
  };

  useEffect(() => { return () => { stopScan(); }; }, []);

  return (
    <div className={className || ''}>
      <div className="flex items-center gap-2 mb-2">
        <label className="text-xs text-muted-foreground">Cámara:</label>
        <select value={selectedDeviceId || ''} onChange={e => setSelectedDeviceId(e.target.value || null)} className="border rounded p-1 text-sm">
          {devices.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || d.deviceId}</option>)}
        </select>
        <Button variant="outline" onClick={enumerateDevices}>Reintentar</Button>
        <Button onClick={() => { setShowCamera(true); startScan(selectedDeviceId); }}>Iniciar cámara</Button>
        <Button variant="outline" onClick={() => { setShowCamera(false); stopScan(); }}>Detener</Button>
      </div>
      {showCamera && (
        <>
          <div className="w-full bg-black h-64 flex items-center justify-center overflow-hidden rounded relative">
            <video ref={videoRef} className="w-full h-full object-cover" playsInline muted autoPlay />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <div aria-hidden className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-3/4 h-3/4 border-2 border-white/70 rounded-md" style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.25) inset' }} />
            </div>
          </div>
          <div className="mt-2 text-sm text-muted-foreground">Alineá el código dentro del recuadro. El escaneo empezará automáticamente si tu navegador lo soporta.</div>
        </>
      )}
    </div>
  );
}
